import { useCallback, useEffect, useState } from "react";
import { getUserEmail, getUserId } from "../utils/auth";

// Drafts are per user, so someone else signing in on the same browser does not see them
const storageKey = (key) => `hec-draft:${getUserId() || getUserEmail() || "anonymous"}:${key}`;

const read = (key, initial) => {
  try {
    const stored = localStorage.getItem(storageKey(key));
    if (!stored) return initial;
    const value = JSON.parse(stored);
    // A form draft saved before a field was added gets that field's default
    const isForm = (v) => v && typeof v === "object" && !Array.isArray(v);
    return isForm(initial) && isForm(value) ? { ...initial, ...value } : value;
  } catch (error) {
    console.warn(`Ignoring unreadable draft "${key}"`, error);
    return initial;
  }
};

// The drafts whose key starts with `prefix`, as { key, value }: to save several forms at once
export const draftsStartingWith = (prefix) => {
  const full = storageKey(prefix);
  const found = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const stored = localStorage.key(i);
      if (stored?.startsWith(full)) found.push({ key: stored.slice(full.length - prefix.length), value: JSON.parse(localStorage.getItem(stored)) });
    }
  } catch (error) {
    console.warn(`Could not read the drafts "${prefix}…"`, error);
  }
  return found;
};

export const removeDraft = (key) => {
  try {
    localStorage.removeItem(storageKey(key));
  } catch (error) {
    console.warn(`Could not clear draft "${key}"`, error);
  }
};

/**
 * Form state that survives closing a dialog or reloading the page. It stays in localStorage until
 * `clear` is called (on cancel or after a successful submit), and an untouched form stores nothing.
 * A different `key` switches to that draft, e.g. one per scholarship. `initial` must be a constant.
 */
export const useDraft = (key, initial) => {
  // The key travels with the value, so a draft is never written under another draft's key
  const [state, setState] = useState(() => ({ key, value: read(key, initial) }));
  let current = state;
  if (state.key !== key) {
    current = { key, value: read(key, initial) };
    setState(current);
  }

  useEffect(() => {
    try {
      if (JSON.stringify(state.value) === JSON.stringify(initial)) localStorage.removeItem(storageKey(state.key));
      else localStorage.setItem(storageKey(state.key), JSON.stringify(state.value));
    } catch (error) {
      console.warn(`Could not save draft "${state.key}"`, error);
    }
  }, [state, initial]);

  const setValue = useCallback(
    (next) => setState((prev) => ({ key: prev.key, value: typeof next === "function" ? next(prev.value) : next })),
    []
  );

  // Removed right away: when the dialog closes in the same update, the key changes before the
  // effect above runs, so it would never see this draft's empty value
  const clear = useCallback(() => {
    try {
      localStorage.removeItem(storageKey(key));
    } catch (error) {
      console.warn(`Could not clear draft "${key}"`, error);
    }
    setState({ key, value: initial });
  }, [key, initial]);

  return [current.value, setValue, clear];
};
